/**
 * In-session memory graph for Pip. Augment only.
 * Claim-level nodes, directed typed edges, cheap then deep ingestion.
 * No cross-session store. Linguistic intensity is not a feeling to speak.
 *
 * Intensity (0-1), linguistic cues only:
 *   0.15
 *   + 0.20 * min(1, wordCount/40)
 *   + 0.25 if the line has "!" or really/always/never/still
 *   + 0.10 if it has every/always/never
 *   + 0.15 if the same canon subject was already seen
 * priorityScore:
 *   0.35*intensity + 0.25*(open|circling) + 0.20*articulationWeight + 0.20*recency
 * Contradiction surfaceWorth (orthogonal to confidence):
 *   0.90 * (1 - evolutionLikely). Surface only when surfaceWorth >= 0.62
 *   AND evolutionLikely <= 0.35. reframes/softens stay at 0.18 and do not nudge.
 */

export const CHEAP_BUDGET_MS = 15;
export const DEEP_BUDGET_MS = 50;
export const SURFACE_WORTH_HIGH = 0.62;
export const EVOLUTION_LOW_MAX = 0.35;
export const SURFACE_SCORE_MIN = 0.55;
export const NEAR_MISS_WAIT = 3;
export const CIRCLE_STUCK_MIN = 3;

export const EDGE_TYPES = [
  'contradicts',
  'elaborates',
  'circles_back_to',
  'resolves',
  'softens',
  'reframes',
  'same_episode',
];

const CANON = {
  dad: 'father', father: 'father', daddy: 'father',
  mom: 'mother', mother: 'mother', mama: 'mother',
  uncle: 'uncle', aunt: 'aunt',
  brother: 'brother', sister: 'sister',
  grandma: 'grandmother', grandmother: 'grandmother',
  grandpa: 'grandfather', grandfather: 'grandfather',
  husband: 'husband', wife: 'wife',
};

const MALE = new Set(['father', 'uncle', 'brother', 'grandfather', 'husband']);
const FEMALE = new Set(['mother', 'aunt', 'sister', 'grandmother', 'wife']);

function clamp(n) {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function round2(n) {
  return Math.round(clamp(n) * 100) / 100;
}

function iso() {
  return new Date().toISOString();
}

function canonOf(word) {
  return CANON[String(word || '').toLowerCase()] || null;
}

function genderOf(canon) {
  if (MALE.has(canon)) return 'm';
  if (FEMALE.has(canon)) return 'f';
  return null;
}

export function createSession(sessionId = 'session') {
  return {
    sessionId: String(sessionId || 'session'),
    updatedAt: iso(),
    nodes: [],
    edges: [],
    stale: false,
    deepPending: false,
    _seq: 0,
    _eseq: 0,
    _males: [],
    _females: [],
  };
}

export function linguisticIntensity(text, seenCanon) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  const n = words.length;
  let s = 0.15;
  s += 0.2 * Math.min(1, n / 40);
  if (/!/.test(text) || /\b(really|always|never|still)\b/i.test(text)) s += 0.25;
  if (/\b(every|always|never)\b/i.test(text)) s += 0.1;
  if (seenCanon) s += 0.15;
  return round2(s);
}

function explicitKinship(text) {
  const m = String(text || '').match(/\b(?:my\s+)?(dad|father|daddy|mom|mother|mama|uncle|aunt|brother|sister|grandma|grandmother|grandpa|grandfather|husband|wife)\b/i);
  return m ? m[1] : null;
}

function resolveSubject(text, graph) {
  const kin = explicitKinship(text);
  if (kin) {
    const canon = canonOf(kin);
    return { surface: kin, canon, gender: genderOf(canon), explicit: true };
  }
  if (/\bhe\b/i.test(text) && graph._males.length === 1) {
    const s = graph._males[0];
    return { surface: s.surface, canon: s.canon, gender: 'm', explicit: false };
  }
  if (/\bshe\b/i.test(text) && graph._females.length === 1) {
    const s = graph._females[0];
    return { surface: s.surface, canon: s.canon, gender: 'f', explicit: false };
  }
  if (/\bwe\b/i.test(text)) return { surface: 'we', canon: null, gender: null, explicit: true };
  if (/\bI\b/.test(text)) return { surface: 'I', canon: null, gender: null, explicit: true };
  if (/\bhe\b/i.test(text)) return { surface: 'he', canon: null, gender: 'm', explicit: false };
  if (/\bshe\b/i.test(text)) return { surface: 'she', canon: null, gender: 'f', explicit: false };
  return { surface: null, canon: null, gender: null, explicit: false };
}

function rememberPerson(graph, resolved) {
  if (!resolved.explicit || !resolved.canon) return;
  const list = resolved.gender === 'm' ? graph._males : resolved.gender === 'f' ? graph._females : null;
  if (!list) return;
  if (list.some((p) => p.canon === resolved.canon)) return;
  list.push({ surface: resolved.surface, canon: resolved.canon });
}

export function deicticReturn(text) {
  return /\b(out there|back there|back to that|back to it|that night|that morning|the same|come back|go back)\b/i.test(text);
}

export function isFigurativeNonClaim(text) {
  const held = /\b(held|holding|holds)\b/i.test(text);
  const weather = /\b(day|afternoon|morning|evening|weather|light|sky)\b/i.test(text);
  const person = /\b(dad|father|mom|mother|uncle|aunt|i|we|he|she)\b/i.test(text);
  return held && weather && !person;
}

function gestureClause(text) {
  const m = String(text || '').match(/^(.*)\b(anyway|never mind|forget it)\b/i);
  if (!m) return null;
  const before = m[1].trim().replace(/[,\s]+$/, '');
  if (!before) return null;
  if (!/\b(i started to|i was going to|i almost|i meant to|what was in|i have not said|i haven't said)\b/i.test(before)) return null;
  return before;
}

function familyOf(text) {
  const stay = /\b(still\s+(?:in|at|home|there)|had\s+not\s+gone|had\s+not\s+left|hadn'?t\s+gone|hadn'?t\s+left|was\s+still\s+home)\b/i.test(text);
  const leave = /\b(moved\s+out|left\s+the\s+house|left\s+in|left\s+to|went\s+to\s+the\s+coast)\b/i.test(text);
  if (stay && /\b(used to say|had the year wrong|looking back)\b/i.test(text)) return 'stayed';
  if (stay) return 'stayed';
  if (leave) return 'left';
  return null;
}

function yearFor(text, family) {
  const years = [...String(text || '').matchAll(/\b(?:19|20)\d{2}\b/g)].map((m) => Number(m[0]));
  if (!years.length) return null;
  if (family === 'stayed') return years[years.length - 1];
  return years[0];
}

function adjectiveComplement(text) {
  const m = String(text || '').match(/\b(?:was|were)\s+(?:not\s+)?([a-z]+)\b/i);
  if (!m) return null;
  if (/^(still|not|in|at|the|a|an|home|there|gone|just|only|very|really|always|never)$/i.test(m[1])) return null;
  return m[1].toLowerCase();
}

function predicateOf(text, family) {
  if (family === 'left') return 'left';
  if (family === 'stayed') return 'stayed';
  const m = String(text || '').match(/\b(was|were|worked|walked|kept|sat|played|went|took|moved|spent|let|started|packed|sent)\b/i);
  return m ? m[1].toLowerCase() : null;
}

function timeOrPlaceOf(text, year) {
  const house = String(text || '').match(/\b(?:in|at)\s+the\s+house\b/i);
  const coast = String(text || '').match(/\bto\s+the\s+coast\b/i);
  const parts = [];
  if (year) parts.push(String(year));
  if (house) parts.push(house[0]);
  else if (coast) parts.push('the coast');
  return parts.length ? parts.join(' ') : null;
}

function topicOf(text) {
  const place = String(text || '').match(/\bmy\s+[a-z]+'s\s+place\b/i);
  if (place) return place[0];
  const loc = String(text || '').match(/\b(kitchen door|porch|dock|lantern|water|quiet|coast|house|mill)\b/i);
  if (loc) return loc[0];
  const kin = explicitKinship(text);
  if (kin) return kin;
  const noun = String(text || '').match(/\b[a-z]{4,}\b/i);
  return noun ? noun[0] : 'that';
}

function evidenceOf(text) {
  const parts = String(text || '').split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);
  const snippets = (parts.length ? parts : [String(text || '').trim()]).slice(0, 3);
  return snippets.filter(Boolean);
}

function evolutionScore(text) {
  let s = 0.1;
  if (/\blooking back\b/i.test(text)) s += 0.35;
  if (/\bi used to (?:say|think)\b/i.test(text)) s += 0.2;
  if (/\b(i had the year wrong|i was wrong|now i see|i see)\b/i.test(text)) s += 0.25;
  if (/\bnot\s+[a-z]+\b/i.test(text) && /\b(looking back|used to)\b/i.test(text)) s += 0.15;
  return round2(s);
}

function articulationWeight(articulation) {
  if (articulation === 'gestured') return 0.9;
  if (articulation === 'hinted') return 0.7;
  if (articulation === 'withdrawn') return 0.3;
  return 0.55;
}

function priorityScore(node, currentTurn) {
  const openish = node.status === 'open' || node.status === 'circling' ? 1 : 0.2;
  const since = Math.max(0, currentTurn - node.lastMentionTurn);
  const recency = 1 - Math.min(since, 12) / 12;
  return round2(0.35 * node.intensity + 0.25 * openish + 0.2 * articulationWeight(node.articulation) + 0.2 * recency);
}

function ignorable(text) {
  return /^(ok|okay|yes|no|yeah|yep|nope|good|tired|fine|sure|thanks|thank you)\.?$/i.test(String(text || '').trim());
}

export function cheapIngestTurn(graph, { turnIndex, text }) {
  const raw = String(text || '').trim();
  if (!raw || ignorable(raw)) return graph;
  const resolved = resolveSubject(raw, graph);
  const seen = !!(resolved.canon && graph.nodes.some((n) => n._canon === resolved.canon));
  rememberPerson(graph, resolved);
  const gestured = gestureClause(raw);
  const figurative = isFigurativeNonClaim(raw);
  const family = familyOf(raw);
  const year = yearFor(raw, family);
  const comp = gestured ? null : adjectiveComplement(raw);
  let kind = 'detail_claim';
  let articulation = 'fully_named';
  let claim = raw;
  if (figurative) {
    kind = 'context_only';
  } else if (gestured) {
    kind = 'near_miss';
    articulation = 'gestured';
    claim = gestured;
  } else if (/^\s*(never mind|forget it|i do not want to talk about that|i don't want to talk about that)\b/i.test(raw)) {
    kind = 'near_miss';
    articulation = 'withdrawn';
  } else if (!resolved.surface && !deicticReturn(raw)) {
    kind = 'context_only';
  } else if (/\b(sort of|kind of)\b/i.test(raw)) {
    articulation = 'hinted';
  }
  graph._seq += 1;
  const node = {
    id: 'n' + graph._seq,
    topic: topicOf(gestured || raw),
    claim,
    claimSlots: {
      subject: gestured ? (explicitKinship(gestured) || (/\bI\b/.test(gestured) ? 'I' : null)) : (resolved.surface || null),
      predicate: gestured ? 'started' : predicateOf(raw, family),
      objectOrComplement: gestured ? null : comp,
      timeOrPlace: gestured ? null : timeOrPlaceOf(raw, year),
    },
    articulation,
    intensity: linguisticIntensity(raw, seen),
    firstMentionTurn: turnIndex,
    lastMentionTurn: turnIndex,
    relatedTurnIndices: [turnIndex],
    evidenceSnippets: evidenceOf(raw),
    status: 'open',
    kind,
    priorityScore: 0,
    lastSurfacedTurn: null,
    narrativeRole: kind === 'near_miss' ? 'unsettled' : 'stable_fact',
    _canon: kind === 'near_miss' ? null : resolved.canon,
    _family: family,
    _year: year,
    _comp: comp,
    _figurative: figurative,
    _text: raw,
  };
  graph.nodes.push(node);
  graph.updatedAt = iso();
  return graph;
}

function addEdge(graph, fields) {
  const dup = graph.edges.some((e) => e.type === fields.type && e.fromNodeId === fields.from && e.toNodeId === fields.to);
  if (dup) return;
  graph._eseq += 1;
  graph.edges.push({
    id: 'e' + graph._eseq,
    fromNodeId: fields.from,
    toNodeId: fields.to,
    type: fields.type,
    confidence: round2(fields.confidence),
    surfaceWorth: round2(fields.surfaceWorth),
    evolutionLikely: round2(fields.evolutionLikely),
    rationale: fields.rationale,
    createdTurn: fields.createdTurn,
    evidenceTurnIndices: fields.evidenceTurnIndices,
  });
}

function conflictKind(a, b) {
  if (!a._canon || a._canon !== b._canon) return null;
  if (a._figurative || b._figurative) return null;
  if (a.kind === 'context_only' || b.kind === 'context_only') return null;
  if (a.kind === 'near_miss' || b.kind === 'near_miss') return null;
  const yearsOk = a._year != null && b._year != null && Math.abs(a._year - b._year) >= 2;
  const presence = yearsOk && ((a._family === 'left' && b._family === 'stayed') || (a._family === 'stayed' && b._family === 'left'));
  if (presence) return 'presence';
  if (a._comp && b._comp && a._comp !== b._comp) return 'frame';
  return null;
}

export function deepIngest(graph, opts = {}) {
  const budgetMs = opts.budgetMs == null ? DEEP_BUDGET_MS : opts.budgetMs;
  const started = Date.now();
  if (budgetMs <= 0) {
    graph.stale = true;
    graph.updatedAt = iso();
    return graph;
  }
  const over = () => (Date.now() - started) > budgetMs;
  graph.edges = [];
  const episodes = [];
  let active = null;
  let left = false;
  const nodes = [...graph.nodes].sort((a, b) => a.firstMentionTurn - b.firstMentionTurn || a.id.localeCompare(b.id));

  const targetFor = (node) => {
    if (node._canon) {
      const found = episodes.find((ep) => ep.canon === node._canon);
      if (found) return found;
    }
    if (deicticReturn(node._text || '')) return episodes.find((ep) => ep.canon) || null;
    return null;
  };

  for (const node of nodes) {
    if (over()) {
      graph.stale = true;
      break;
    }
    if (node.kind === 'near_miss' || node._figurative || node.kind === 'context_only') {
      if (active) left = true;
      active = null;
      continue;
    }
    const target = targetFor(node);
    if (target && target.seedId !== node.id) {
      const returned = left || (active && active !== target);
      if (returned) {
        addEdge(graph, {
          from: node.id,
          to: target.seedId,
          type: 'circles_back_to',
          confidence: 0.58,
          surfaceWorth: 0.72,
          evolutionLikely: 0.12,
          rationale: 'Internal: came back after another thread via a pronoun or a deictic, not a repeated topic label.',
          createdTurn: node.firstMentionTurn,
          evidenceTurnIndices: [target.lastTurn, node.firstMentionTurn],
        });
        addEdge(graph, {
          from: node.id,
          to: target.seedId,
          type: 'same_episode',
          confidence: 0.6,
          surfaceWorth: 0.2,
          evolutionLikely: 0.12,
          rationale: 'Internal: same episode as the earlier thread.',
          createdTurn: node.firstMentionTurn,
          evidenceTurnIndices: [target.lastTurn, node.firstMentionTurn],
        });
      } else {
        addEdge(graph, {
          from: node.id,
          to: target.seedId,
          type: 'elaborates',
          confidence: 0.62,
          surfaceWorth: 0.2,
          evolutionLikely: 0.1,
          rationale: 'Internal: adds a compatible detail on the same person or episode.',
          createdTurn: node.firstMentionTurn,
          evidenceTurnIndices: [target.lastTurn, node.firstMentionTurn],
        });
      }
      target.lastTurn = node.firstMentionTurn;
      active = target;
      left = false;
      continue;
    }
    const ep = {
      id: 'ep' + episodes.length,
      seedId: node.id,
      canon: node._canon,
      lastTurn: node.firstMentionTurn,
    };
    episodes.push(ep);
    active = ep;
    left = false;
  }

  const details = nodes.filter((n) => n._canon && n.kind !== 'context_only' && n.kind !== 'near_miss' && !n._figurative);
  for (let i = 0; i < details.length; i++) {
    if (over()) {
      graph.stale = true;
      break;
    }
    for (let j = i + 1; j < details.length; j++) {
      const a = details[i];
      const b = details[j];
      const kind = conflictKind(a, b);
      if (!kind) continue;
      const evo = evolutionScore(b._text || b.claim);
      if (kind === 'frame' && evo >= 0.5) {
        const soft = /\b(wasn'?t cruel|just tired|too hard on)\b/i.test(b._text || '') && !/\b(looking back|now i see|i see)\b/i.test(b._text || '');
        addEdge(graph, {
          from: b.id,
          to: a.id,
          type: soft ? 'softens' : 'reframes',
          confidence: 0.72,
          surfaceWorth: 0.18,
          evolutionLikely: evo,
          rationale: 'Internal: later words revise an earlier framing. Do not treat it as a mistake.',
          createdTurn: b.firstMentionTurn,
          evidenceTurnIndices: [a.firstMentionTurn, b.firstMentionTurn],
        });
      } else if (kind === 'presence' || kind === 'frame') {
        addEdge(graph, {
          from: b.id,
          to: a.id,
          type: 'contradicts',
          confidence: kind === 'presence' ? 0.66 : 0.5,
          surfaceWorth: round2(0.9 * (1 - evo)),
          evolutionLikely: evo,
          rationale: 'Internal: two tellings of the same person do not sit together. Not for reading aloud.',
          createdTurn: b.firstMentionTurn,
          evidenceTurnIndices: [a.firstMentionTurn, b.firstMentionTurn],
        });
      }
    }
  }

  const circleCounts = {};
  for (const e of graph.edges) {
    if (e.type !== 'circles_back_to') continue;
    circleCounts[e.toNodeId] = (circleCounts[e.toNodeId] || 0) + 1;
  }
  const currentTurn = nodes.reduce((m, n) => Math.max(m, n.lastMentionTurn), 0);
  for (const node of graph.nodes) {
    const circles = circleCounts[node.id] || 0;
    if (circles >= 2) {
      node.status = 'circling';
      if (node.kind === 'detail_claim') node.kind = 'circling_topic';
    }
    const touch = graph.edges.filter((e) => e.fromNodeId === node.id || e.toNodeId === node.id);
    if (touch.some((e) => e.type === 'reframes' || e.type === 'softens' || e.evolutionLikely >= 0.7)) {
      node.narrativeRole = 'evolving_framing';
    } else if (node.kind === 'near_miss' || touch.some((e) => e.type === 'contradicts' && e.evolutionLikely < 0.5)) {
      node.narrativeRole = 'unsettled';
    } else if (node.kind !== 'near_miss') {
      node.narrativeRole = 'stable_fact';
    }
    node.priorityScore = priorityScore(node, currentTurn);
  }
  graph.updatedAt = iso();
  return graph;
}

export function scheduleDeepPass(graph) {
  graph.deepPending = true;
  const timer = setTimeout(() => {
    try {
      deepIngest(graph);
    } finally {
      graph.deepPending = false;
      graph.updatedAt = iso();
    }
  }, 0);
  return timer;
}

export function ingestTurns(graph, userTexts, opts = {}) {
  const budget = opts.cheapBudgetMs == null ? CHEAP_BUDGET_MS : opts.cheapBudgetMs;
  if (budget <= 0) {
    graph.stale = true;
    graph.updatedAt = iso();
    return graph;
  }
  const started = Date.now();
  const texts = userTexts || [];
  for (let i = 0; i < texts.length; i++) {
    if ((Date.now() - started) > budget) {
      graph.stale = true;
      break;
    }
    cheapIngestTurn(graph, { turnIndex: i, text: texts[i] });
  }
  if (opts.deep === 'skip' || graph.stale) return graph;
  if (opts.deep === 'async') {
    scheduleDeepPass(graph);
    return graph;
  }
  deepIngest(graph, { budgetMs: opts.deepBudgetMs == null ? DEEP_BUDGET_MS : opts.deepBudgetMs });
  return graph;
}

export function neighbors(graph, nodeId, opts = {}) {
  const direction = opts.direction || 'both';
  const types = opts.edgeTypes ? new Set(opts.edgeTypes) : null;
  const out = [];
  for (const edge of graph.edges || []) {
    if (types && !types.has(edge.type)) continue;
    if ((direction === 'out' || direction === 'both') && edge.fromNodeId === nodeId) {
      out.push({ nodeId: edge.toNodeId, edge, direction: 'out' });
    }
    if ((direction === 'in' || direction === 'both') && edge.toNodeId === nodeId) {
      out.push({ nodeId: edge.fromNodeId, edge, direction: 'in' });
    }
  }
  return out;
}

export function subgraph(graph, seedNodeId, opts = {}) {
  const maxDepth = opts.maxDepth == null ? 2 : opts.maxDepth;
  const seen = new Set([seedNodeId]);
  const edgeIds = new Set();
  let frontier = [seedNodeId];
  for (let d = 0; d < maxDepth; d++) {
    const next = [];
    for (const id of frontier) {
      for (const hop of neighbors(graph, id, { edgeTypes: opts.edgeTypes, direction: 'both' })) {
        edgeIds.add(hop.edge.id);
        if (!seen.has(hop.nodeId)) {
          seen.add(hop.nodeId);
          next.push(hop.nodeId);
        }
      }
    }
    frontier = next;
  }
  return {
    nodes: (graph.nodes || []).filter((n) => seen.has(n.id)),
    edges: (graph.edges || []).filter((e) => edgeIds.has(e.id)),
  };
}

export function path(graph, fromId, toId, opts = {}) {
  const maxDepth = opts.maxDepth == null ? 6 : opts.maxDepth;
  if (fromId === toId) return [fromId];
  const q = [[fromId]];
  const seen = new Set([fromId]);
  while (q.length) {
    const p = q.shift();
    if (p.length - 1 >= maxDepth) continue;
    const tip = p[p.length - 1];
    for (const hop of neighbors(graph, tip, { edgeTypes: opts.edgeTypes, direction: 'both' })) {
      if (p.includes(hop.nodeId)) continue;
      const next = p.concat(hop.nodeId);
      if (hop.nodeId === toId) return next;
      if (!seen.has(hop.nodeId)) {
        seen.add(hop.nodeId);
        q.push(next);
      }
    }
  }
  return null;
}

function contradictionHint(graph, edge) {
  const node = (graph.nodes || []).find((n) => n.id === edge.fromNodeId) || (graph.nodes || []).find((n) => n.id === edge.toNodeId);
  const topic = (node && node.topic) || 'that';
  return `One open thread on ${topic}: two timings are both in their words. Ask which timing they want in the story. Do not accuse them of getting it wrong.`;
}

function nearMissHint(node) {
  const quote = (node.evidenceSnippets && node.evidenceSnippets[0]) || node.claim || '';
  const named = quote.match(/\bthe\s+[a-z]+\s+[a-z]+\b/i);
  const crumb = named ? named[0] : 'what they started to say';
  return `They started to say something about ${crumb} and moved on. One soft question may invite them back. Do not guess anything they did not say.`;
}

export function recommendSurface(graph, opts = {}) {
  const nodes = graph.nodes || [];
  const currentTurn = opts.currentTurn == null
    ? nodes.reduce((m, n) => Math.max(m, n.lastMentionTurn || 0), 0)
    : opts.currentTurn;
  const touched = opts.touchedNodeIds || nodes.filter((n) => (n.relatedTurnIndices || []).includes(currentTurn)).map((n) => n.id);
  const local = new Set(touched);
  for (const id of touched) {
    const sub = subgraph(graph, id, { maxDepth: 2 });
    sub.nodes.forEach((n) => local.add(n.id));
  }
  const candidates = [];
  for (const edge of graph.edges || []) {
    const inView = !touched.length || local.has(edge.fromNodeId) || local.has(edge.toNodeId);
    if (!inView) continue;
    if (edge.type === 'contradicts') {
      if (edge.surfaceWorth >= SURFACE_WORTH_HIGH && edge.evolutionLikely <= EVOLUTION_LOW_MAX) {
        candidates.push({
          tier: 2,
          score: edge.surfaceWorth * (1 - edge.evolutionLikely),
          edge,
          nodeId: edge.fromNodeId,
          askHint: contradictionHint(graph, edge),
        });
      }
    } else if (edge.type === 'circles_back_to') {
      const stuck = graph.edges.filter((e) => e.type === 'circles_back_to' && e.toNodeId === edge.toNodeId).length;
      if (stuck >= CIRCLE_STUCK_MIN) {
        const hubNode = nodes.find((n) => n.id === edge.toNodeId);
        candidates.push({
          tier: 1,
          score: 0.6,
          edge,
          nodeId: edge.toNodeId,
          askHint: `They keep returning to ${(hubNode && hubNode.topic) || 'that'} without landing. Ask one concrete question using their words. One question only.`,
        });
      }
    }
  }
  for (const node of nodes) {
    if (node.kind !== 'near_miss' || node.articulation === 'withdrawn') continue;
    if (node.status === 'resolved') continue;
    if (currentTurn - node.firstMentionTurn < NEAR_MISS_WAIT) continue;
    if (node.lastSurfacedTurn != null) continue;
    candidates.push({
      tier: 1,
      score: 0.66,
      edge: null,
      nodeId: node.id,
      askHint: nearMissHint(node),
    });
  }
  if (!candidates.length) return null;
  candidates.sort((a, b) => b.tier - a.tier || b.score - a.score);
  const top = candidates[0];
  if (top.score < SURFACE_SCORE_MIN) return null;
  const rec = { nodeId: top.nodeId, askHint: top.askHint };
  if (top.edge) rec.edgeId = top.edge.id;
  return rec;
}

function scrub(s) {
  return String(s || '').replace(/â€”/g, ',').replace(/---/g, ' ').replace(/\s+/g, ' ').trim();
}

export function renderContextBlock(graph, opts = {}) {
  if (!graph || !graph.nodes || !graph.nodes.length) return '';
  const rec = recommendSurface(graph, opts);
  const ranked = [...graph.nodes].sort((a, b) => b.priorityScore - a.priorityScore).slice(0, 5);
  const ids = new Set(ranked.map((n) => n.id));
  if (rec) ids.add(rec.nodeId);
  let edges = graph.edges.filter((e) => ids.has(e.fromNodeId) || ids.has(e.toNodeId)).slice(0, 8);
  if (rec && rec.edgeId && !edges.some((e) => e.id === rec.edgeId)) {
    const extra = graph.edges.find((e) => e.id === rec.edgeId);
    if (extra) edges = edges.concat(extra);
  }
  const nodeLines = ranked.map((n) => (
    `- id=${n.id} topic="${scrub(n.topic)}" claim="${scrub(n.claim)}" status=${n.status} articulation=${n.articulation} kind=${n.kind}`
  ));
  const edgeLines = edges.map((e) => (
    `- id=${e.id} type=${e.type} from=${e.fromNodeId} to=${e.toNodeId} confidence=${e.confidence} surfaceWorth=${e.surfaceWorth} evolutionLikely=${e.evolutionLikely}`
  ));
  const recLine = rec
    ? `recommendedSurface: ${JSON.stringify({ nodeId: rec.nodeId, edgeId: rec.edgeId || null, askHint: scrub(rec.askHint) })}`
    : 'recommendedSurface: none';
  return [
    'MEMORY GRAPH (internal context only. Do not read this aloud. Do not accuse. Do not invent anything they did not say. Linguistic intensity is not a feeling to name.)',
    'Open nodes:',
    nodeLines.join('\n') || '- none',
    'Edges in view:',
    edgeLines.join('\n') || '- none',
    recLine,
  ].join('\n');
}

export function contextBlockForConversation(conversation) {
  const texts = [];
  for (const turn of conversation || []) {
    if (turn && turn.role === 'user' && turn.content) texts.push(String(turn.content));
  }
  if (!texts.length) return '';
  const graph = createSession('live');
  ingestTurns(graph, texts, { deep: 'sync' });
  return renderContextBlock(graph);
}

const NODE_KEYS = ['id', 'topic', 'claim', 'claimSlots', 'articulation', 'intensity', 'firstMentionTurn', 'lastMentionTurn', 'relatedTurnIndices', 'evidenceSnippets', 'status', 'kind', 'priorityScore', 'lastSurfacedTurn', 'narrativeRole'];
const EDGE_KEYS = ['id', 'fromNodeId', 'toNodeId', 'type', 'confidence', 'surfaceWorth', 'evolutionLikely', 'rationale', 'createdTurn', 'evidenceTurnIndices'];

function pick(obj, keys) {
  const out = {};
  keys.forEach((k) => { out[k] = obj[k]; });
  return out;
}

export function sessionView(graph) {
  return {
    sessionId: graph.sessionId,
    updatedAt: graph.updatedAt,
    nodes: (graph.nodes || []).map((n) => pick(n, NODE_KEYS)),
    edges: (graph.edges || []).map((e) => pick(e, EDGE_KEYS)),
  };
}
